import { useState, useRef, useEffect } from 'react';
import { Mic, MicOff, Copy, Download, AlertCircle } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { copyToClipboard, download } from '@/lib/utils';
import { toast } from 'sonner';

const LANGS = [
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'en-IN', label: 'English (India)' },
  { value: 'hi-IN', label: 'Hindi' },
  { value: 'es-ES', label: 'Spanish' },
  { value: 'fr-FR', label: 'French' },
  { value: 'de-DE', label: 'German' },
  { value: 'it-IT', label: 'Italian' },
  { value: 'pt-BR', label: 'Portuguese (BR)' },
  { value: 'ja-JP', label: 'Japanese' },
  { value: 'zh-CN', label: 'Chinese (simplified)' },
  { value: 'ar-SA', label: 'Arabic' },
];

interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  onresult: ((e: { resultIndex: number; results: ArrayLike<{ isFinal: boolean; 0: { transcript: string } }> }) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
}

export function SpeechToText() {
  const [supported, setSupported] = useState(true);
  const [recording, setRecording] = useState(false);
  const [final, setFinal] = useState('');
  const [interim, setInterim] = useState('');
  const [lang, setLang] = useState('en-US');
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);

  useEffect(() => {
    const w = window as unknown as { SpeechRecognition?: new () => SpeechRecognitionLike; webkitSpeechRecognition?: new () => SpeechRecognitionLike };
    const Cls = w.SpeechRecognition ?? w.webkitSpeechRecognition;
    if (!Cls) {
      setSupported(false);
      return;
    }
    const r = new Cls();
    r.continuous = true;
    r.interimResults = true;
    recognitionRef.current = r;
  }, []);

  function start() {
    const r = recognitionRef.current;
    if (!r) return;
    r.lang = lang;
    r.onresult = (e) => {
      let i = '';
      let f = '';
      for (let idx = e.resultIndex; idx < e.results.length; idx++) {
        const res = e.results[idx];
        if (res.isFinal) f += res[0].transcript + ' ';
        else i += res[0].transcript;
      }
      if (f) setFinal((prev) => prev + f);
      setInterim(i);
    };
    r.onerror = (e) => {
      toast.error(`Error: ${e.error}`);
      setRecording(false);
    };
    r.onend = () => {
      setRecording(false);
      setInterim('');
    };
    try {
      r.start();
      setRecording(true);
    } catch {
      // already started
    }
  }

  function stop() {
    recognitionRef.current?.stop();
    setRecording(false);
  }

  function reset() {
    setFinal('');
    setInterim('');
  }

  if (!supported) {
    return (
      <div>
        <ToolHeader
          toolId="speech-to-text"
          title="Speech to text"
          description="Live dictation using the browser's Web Speech API."
        />
        <Card>
          <div className="flex items-start gap-2 text-warning">
            <AlertCircle size={14} className="mt-0.5" />
            <div className="text-sm">
              <div className="font-semibold">Not available in this browser</div>
              <div className="text-fg-muted text-xs mt-1">
                Web Speech recognition is supported in Chrome, Edge, and Safari. Firefox is unsupported as of 2026.
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const wordCount = final.trim().split(/\s+/).filter(Boolean).length;

  return (
    <div>
      <ToolHeader
        toolId="speech-to-text"
        title="Speech to text"
        description="Live dictation using the browser's Web Speech API. Free, local, and unlimited."
        badges={
          recording ? (
            <Badge variant="danger">
              <span className="size-1.5 rounded-full bg-danger animate-pulse mr-1" />
              recording
            </Badge>
          ) : final ? (
            <Badge variant="success">{wordCount} words</Badge>
          ) : null
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:order-2 space-y-3">
          <Field label="Language">
            <Select
              value={lang}
              onChange={(e) => setLang(e.target.value)}
              options={LANGS}
              disabled={recording}
            />
          </Field>
          {recording ? (
            <Button variant="danger" className="w-full" onClick={stop}>
              <MicOff size={12} />
              Stop recording
            </Button>
          ) : (
            <Button variant="primary" className="w-full" onClick={start}>
              <Mic size={12} />
              Start recording
            </Button>
          )}
          <div className="grid grid-cols-2 gap-2 pt-2 border-t border-border">
            <Button
              size="sm"
              variant="ghost"
              disabled={!final}
              onClick={async () => {
                const ok = await copyToClipboard(final);
                if (ok) toast.success('Copied');
              }}
            >
              <Copy size={11} />
              Copy
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={!final}
              onClick={() => download(final, 'transcript.txt', 'text/plain')}
            >
              <Download size={11} />
              Save
            </Button>
          </div>
          <Button size="sm" variant="ghost" className="w-full" onClick={reset} disabled={!final}>
            Clear
          </Button>
          <div className="text-2xs text-fg-subtle leading-relaxed border-t border-border pt-2">
            Keep this tab focused; some browsers pause recognition when backgrounded.
          </div>
        </Card>

        <Card className="lg:col-span-2 lg:order-1 !p-0">
          <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
            Transcript
          </div>
          <div className="p-5 min-h-[480px] max-h-[600px] overflow-auto text-sm leading-relaxed whitespace-pre-wrap">
            {final}
            {interim && <span className="text-fg-subtle italic">{interim}</span>}
            {!final && !interim && (
              <span className="text-fg-subtle">Click Start recording and speak…</span>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

export default SpeechToText;
